import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select';
import { Textarea } from 'app/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'app/components/ui/card';
import { Badge } from 'app/components/ui/badge';
import { Alert, AlertDescription } from 'app/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table';
import { useInventorySlice } from '../slice';
import { BulkAdjustmentFormProps, BulkAdjustmentItem } from 'types/inventory';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Package } from 'lucide-react';

const BulkAdjustmentDialog: React.FC<BulkAdjustmentFormProps> = ({
    onSuccess,
    onCancel,
}) => {
    const [adjustments, setAdjustments] = useState<BulkAdjustmentItem[]>([
        {
            product_id: 0,
            action_type: 'adjustment',
            quantity_change: 0,
            unit_cost: undefined,
            reference_number: '',
            notes: '',
        }
    ]);

    const [performedBy, setPerformedBy] = useState('admin');
    const { useBulkAdjustInventoryMutation } = useInventorySlice();
    const [bulkAdjust, { isLoading }] = useBulkAdjustInventoryMutation();

    const addAdjustment = () => {
        setAdjustments(prev => [...prev, {
            product_id: 0,
            action_type: 'adjustment',
            quantity_change: 0,
            unit_cost: undefined,
            reference_number: '',
            notes: '',
        }]);
    };

    const removeAdjustment = (index: number) => {
        if (adjustments.length > 1) {
            setAdjustments(prev => prev.filter((_, i) => i !== index));
        }
    };

    const updateAdjustment = (index: number, field: keyof BulkAdjustmentItem, value: any) => {
        setAdjustments(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate adjustments
        const validAdjustments = adjustments.filter(adj =>
            adj.product_id > 0 && adj.quantity_change !== 0
        );

        if (validAdjustments.length === 0) {
            toast.error('Please add at least one valid adjustment');
            return;
        }

        try {
            const result = await bulkAdjust({
                adjustments: validAdjustments,
                performed_by: performedBy,
            }).unwrap();

            const successCount = result.successful?.length || 0;
            const failCount = result.failed?.length || 0;

            if (successCount > 0) {
                toast.success(`Bulk adjustment completed. ${successCount} successful, ${failCount} failed`);
            }

            if (failCount > 0) {
                toast.error(`${failCount} adjustments failed`);
            }

            onSuccess?.();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to perform bulk adjustment');
        }
    };

    const getActionTypeIcon = (actionType: string) => {
        switch (actionType) {
            case 'adjustment':
                return <Package className="h-4 w-4" />;
            case 'sale':
                return <Package className="h-4 w-4" />;
            case 'return':
                return <Package className="h-4 w-4" />;
            case 'damage':
                return <Package className="h-4 w-4" />;
            case 'transfer':
                return <Package className="h-4 w-4" />;
            default:
                return <Package className="h-4 w-4" />;
        }
    };

    return (
        <Dialog open onOpenChange={onCancel}>
            <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Bulk Inventory Adjustment</DialogTitle>
                    <DialogDescription>
                        Adjust inventory for multiple products at once
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Performed By */}
                    <div className="space-y-2">
                        <Label htmlFor="performed_by">Performed By</Label>
                        <Input
                            id="performed_by"
                            value={performedBy}
                            onChange={(e) => setPerformedBy(e.target.value)}
                            placeholder="Enter your name or ID"
                            required
                        />
                    </div>

                    {/* Adjustments Table */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <span>Product Adjustments</span>
                                <Button type="button" onClick={addAdjustment} size="sm">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Product
                                </Button>
                            </CardTitle>
                            <CardDescription>
                                Add products and their adjustment details
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Product ID</TableHead>
                                            <TableHead>Action Type</TableHead>
                                            <TableHead>Quantity Change</TableHead>
                                            <TableHead>Unit Cost</TableHead>
                                            <TableHead>Reference</TableHead>
                                            <TableHead>Notes</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {adjustments.map((adjustment, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Input
                                                        type="number"
                                                        value={adjustment.product_id || ''}
                                                        onChange={(e) => updateAdjustment(index, 'product_id', parseInt(e.target.value) || 0)}
                                                        placeholder="Product ID"
                                                        min="1"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={adjustment.action_type}
                                                        onValueChange={(value) => updateAdjustment(index, 'action_type', value)}
                                                    >
                                                        <SelectTrigger className="w-32">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="adjustment">
                                                                <div className="flex items-center gap-2">
                                                                    {getActionTypeIcon('adjustment')}
                                                                    <span>Adjustment</span>
                                                                </div>
                                                            </SelectItem>
                                                            <SelectItem value="sale">
                                                                <div className="flex items-center gap-2">
                                                                    {getActionTypeIcon('sale')}
                                                                    <span>Sale</span>
                                                                </div>
                                                            </SelectItem>
                                                            <SelectItem value="return">
                                                                <div className="flex items-center gap-2">
                                                                    {getActionTypeIcon('return')}
                                                                    <span>Return</span>
                                                                </div>
                                                            </SelectItem>
                                                            <SelectItem value="damage">
                                                                <div className="flex items-center gap-2">
                                                                    {getActionTypeIcon('damage')}
                                                                    <span>Damage</span>
                                                                </div>
                                                            </SelectItem>
                                                            <SelectItem value="transfer">
                                                                <div className="flex items-center gap-2">
                                                                    {getActionTypeIcon('transfer')}
                                                                    <span>Transfer</span>
                                                                </div>
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        type="number"
                                                        value={adjustment.quantity_change || ''}
                                                        onChange={(e) => updateAdjustment(index, 'quantity_change', parseInt(e.target.value) || 0)}
                                                        placeholder="Quantity"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={adjustment.unit_cost || ''}
                                                        onChange={(e) => updateAdjustment(index, 'unit_cost', parseFloat(e.target.value) || undefined)}
                                                        placeholder="Cost"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        value={adjustment.reference_number || ''}
                                                        onChange={(e) => updateAdjustment(index, 'reference_number', e.target.value)}
                                                        placeholder="Reference"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        value={adjustment.notes || ''}
                                                        onChange={(e) => updateAdjustment(index, 'notes', e.target.value)}
                                                        placeholder="Notes"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => removeAdjustment(index)}
                                                        disabled={adjustments.length === 1}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Validation Summary */}
                    <Alert>
                        <AlertDescription>
                            <div className="space-y-1">
                                <p><strong>Valid adjustments:</strong> {adjustments.filter(adj => adj.product_id > 0 && adj.quantity_change !== 0).length}</p>
                                <p><strong>Invalid adjustments:</strong> {adjustments.filter(adj => adj.product_id <= 0 || adj.quantity_change === 0).length}</p>
                            </div>
                        </AlertDescription>
                    </Alert>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading || adjustments.filter(adj => adj.product_id > 0 && adj.quantity_change !== 0).length === 0}
                        >
                            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Apply Bulk Adjustment
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default BulkAdjustmentDialog;
